import { BookImage, Calendar, ChevronDown, Columns, FileUp, Grid3x3, Image, Minus, TypeOutline } from "lucide-react";

export const NO_SELECTION_TEXT = "Select a field on the canvas to edit its properties";

export const FORM_ICON = {
  TEXT: <TypeOutline />,
  ADVANCED_TEXT: <BookImage />,
  IMAGE: <Image />,
  FILE: <FileUp />,
  DIVIDER: <Minus />,
  TABLE: <Grid3x3 />,
  COLUMNS: <Columns />,
};

export const FORM_ICONS_UPDATED = {
  "text-label": <TypeOutline />,
  "advanced-text": <BookImage />,
  image: <Image />,
  "file-upload": <FileUp />,
  "horizontal-line": <Minus />,
  table: <Grid3x3 />,
  dropdown: <ChevronDown />,
  "row-container": <Columns />,
};

// Form Element Types Constants
export const FORM_ELEMENT_TYPES = {
  TEXT_LABEL: "text-label",
  ADVANCED_TEXT: "advanced-text",
  ROW_CONTAINER: "row-container",
  /** Palette / canvas “user chip” element */
  USER_CHIP: "user chip",
  TABLE: "table",
  IMAGE: "image",
  FILE_UPLOAD: "file-upload",
  HORIZONTAL_LINE: "horizontal-line",
  DROPDOWN: "dropdown",
};

/** Default table column count when `columnCount` is unset (form builder field properties). */
export const TABLE_FIELD_DEFAULT_COLUMN_COUNT = 4;

/** Default font size (px) for text-label elements in the form builder. */
export const TEXT_LABEL_DEFAULT_FONT_SIZE = 16;

/** Font size options (px) for text-label field properties. */
export const TEXT_LABEL_FONT_SIZE_OPTIONS = [12, 14, 16, 18, 20, 24, 28];

/** Kendo DropDownList items for table column count (FieldProperties). */
export const FIELD_PROPERTIES_TABLE_COLUMN_COUNT_ITEMS = [1, 2, 3, 4, 5, 6].map((num) => ({ text: num, value: num }));

/** Kendo DropDownList items for per-column types (FieldProperties). */
export const FIELD_PROPERTIES_TABLE_COLUMN_TYPE_ITEMS = [
  { text: "Text", value: "text" },
  { text: "Number", value: "number" },
  { text: "Date", value: "date" },
  { text: "Dropdown", value: "dropdown" },
  { text: "File Upload", value: "fileUpload" },
];

/** Labels and `title` attributes for the FieldProperties sidebar. */
export const FIELD_PROPERTIES_LABELS = {
  SIZE: "Size",
  STYLE: "Style",
  HORIZONTAL_ALIGN: "Horizontal Align",
  VERTICAL_ALIGN: "Vertical Align",
  TABLE_COLUMNS: "Table Columns",
  COLUMN_TYPES: "Column Types",
  COLUMN_TITLE_PREFIX: "Column ",
  BOLD: "Bold",
  ITALIC: "Italic",
  UNDERLINE: "Underline",
  ALIGN_LEFT: "Align Left",
  ALIGN_CENTER: "Align Center",
  ALIGN_RIGHT: "Align Right",
  VALIGN_TOP: "Align Top",
  VALIGN_MIDDLE: "Align Middle",
  VALIGN_BOTTOM: "Align Bottom",
};

/** Labels, sizes, and titles for TableRenderer (form preview / fill). */
export const TABLE_RENDERER = {
  COLUMN_MIN_WIDTH_PX: 30,
  COLUMN_DEFAULT_WIDTH_PX: 120,
  DELETE_ROW_ACTION_WIDTH_PX: 36,
  ADD_ROW_LABEL: "+ Add Row",
  DELETE_ROW_TITLE: "Delete Row",
  COLUMN_RESIZE_TITLE: "Resize column",
};

// Form Element Properties Constants
export const ELEMENT_PROPERTIES = {
  SMART_CHIP: "smart chip",
  HIDDEN: "isHidden",
  CHIP_TYPE: "chipType",
};

// Form Element CSS Classes
export const FORM_ELEMENT_CLASSES = {
  FORM_ELEMENT: "form-element",
  ROW_ELEMENT: "row-element",
  ELEMENT_PREVIEW: "element-preview",
  ELEMENT_HEADER: "element-header",
  PREVIEW_PLACEHOLDER: "preview-placeholder",
  ELEMENT_TYPE_ICON: "element-type-icon",
  ELEMENT_DELETE_ICON: "element-delete-icon",
  ROW_DELETE_ICON: "row-delete-icon",
};

export const FORM_TABS = {
  SUBSECTIONS_OBJ: {
    title: "Form Section",
    type: "form",
    hostSection: "subsections",
    bgColorKey: "formBgColor",
    bgColor: "#e5f0f5",
  },
  HIDDEN_SUBSECTIONS_OBJ: {
    title: "Review Section",
    type: "review",
    hostSection: "hiddenSubsections",
    bgColorKey: "reviewFormBgColor",
    bgColor: "#FFFAF4",
  },
};

export const FORM_TABS_KEYS = {
  0: "SUBSECTIONS_OBJ",
  1: "HIDDEN_SUBSECTIONS_OBJ",
};

// Table File Upload Modal Constants
export const TABLE_FILE_UPLOAD = {
  TABS: {
    QUICK_ACCESS: "Quick Access",
    RECENT: "Recent",
    UPLOAD_VIA_DEVICE: "Upload via Device",
  },
  FILE_SOURCE: {
    CMS: "cms",
    DEVICE: "device",
  },
  COLUMN_HEADERS: {
    NAME: "Name",
    DATE_CREATED: "Date Created",
    SIZE: "Size",
  },
  LOADING_TEXT: "Loading...",
  EMPTY_TEXT: "No files found",
  DEVICE_HINT_TEXT: "Select a file from your device to attach to this cell.",
  CHOOSE_FILE_BTN: "Choose File",
  CLOSE_ARIA_LABEL: "Close",
  FOLDER_TYPE: "folder",
  FILE_UPLOAD_COLUMN_TYPE: "fileUpload",
}
/** Keys on submitted form payload for header "last updated" line, by `formTab.type` */
export const LAST_UPDATED_KEYS_BY_TAB = {
  [FORM_TABS.SUBSECTIONS_OBJ.type]: {
    lastUpdatedByDisplayNameKey: "lastUpdatedByName",
    lastUpdatedAtKey: "updatedAt",
  },
  [FORM_TABS.HIDDEN_SUBSECTIONS_OBJ.type]: {
    lastUpdatedByDisplayNameKey: "reviewLastUpdatedBy",
    lastUpdatedAtKey: "reviewLastUpdatedAt",
  },
};

  // Color palette settings (same as SubsectionProperties)
  export const PALETTE_SETTINGS = {
    palette: [
      "#FFF7F7",
      "#FFEAEA",
      "#FFDCDC",
      "#FFCECE",
      "#FFC0C0",
      "#FFB3B3",
      "#FFF4FA",
      "#FFE9F3",
      "#FFDDEE",
      "#FFCFE7",
      "#FFBFE0",
      "#FFAFD8",
      "#FFF9F1",
      "#FFF1E0",
      "#FFE9D1",
      "#FFE1C2",
      "#FFD9B3",
      "#FFCCA3",
      "#FFFDF4",
      "#FFF9E8",
      "#FFF3D9",
      "#FFEFC9",
      "#FFE9B8",
      "#FFE3A8",
      "#F8FFF8",
      "#EDFAED",
      "#E0F5E0",
      "#D2EFD2",
      "#C4E9C4",
      "#B8E3B8",
      "#F7FBFF",
      "#ECF5FF",
      "#E1EFFF",
      "#D5E8FF",
      "#CAE2FF",
      "#C0DCFF",
      "#FFFFFF",
      "#F8F8F8",
      "#F0F0F0",
      "#E6E6E6",
      "#DCDCDC",
      "#D3D3D3",
    ],
    columns: 6,
    tileSize: 28,
  };